/**
 * Sprint DS B7: Route Transition Wrapper
 *
 * Wraps page content with framer-motion animations:
 * - Lateral (same level): slide left/right 200ms
 * - Drill-in: scale down 0.98 + fade → scale up from 0.95
 * - Back: reverse, faster 150ms
 */
import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { useReducedMotion } from "@/hooks/use-reduced-motion";

interface PageTransitionProps {
  children: ReactNode;
  mode?: "lateral" | "drill" | "back";
}

const CONFIGS = {
  lateral: { initial: { opacity: 0, x: 20 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -20 }, dur: 0.2 },
  drill: { initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.98 }, dur: 0.2 },
  back: { initial: { opacity: 0, scale: 1.02 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.95 }, dur: 0.15 },
};

export function PageTransition({ children, mode = "lateral" }: PageTransitionProps) {
  const c = CONFIGS[mode];
  const isReducedMotion = useReducedMotion();

  return (
    <motion.div
      initial={isReducedMotion ? false : c.initial}
      animate={c.animate}
      exit={isReducedMotion ? undefined : c.exit}
      transition={{ duration: isReducedMotion ? 0 : c.dur, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {children}
    </motion.div>
  );
}
